import React from "react";
import { useIgSettings } from "./instagram/useIgSettings";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Instagram, Link2, Unplug, CheckCircle2, Info, RefreshCw, Clock, AlertTriangle } from "lucide-react";

export const InstagramConnectCard = () => {
  const ig = useIgSettings();
  const { state, form, setField, busy } = ig;

  return (
    <div className="rounded-xl border border-border bg-card p-6" data-testid="instagram-connect-card">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Instagram className="h-5 w-5 text-primary" strokeWidth={1.75} />
          <h3 className="font-heading text-lg font-semibold">Collega Instagram</h3>
        </div>
        <ConnectionBadge state={state} />
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Con la Instagram Graph API (Business Discovery) i dati reali dei profili prospect — follower, post, like e commenti medi — si aggiornano da soli con un click nella scheda, senza inserirli a mano.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Access Token (long-lived)">
          <Input type="password" value={form.token} onChange={(e) => setField("token", e.target.value)}
            placeholder={state.token_masked ? `Salvato: ${state.token_masked}` : "EAAB…"} data-testid="ig-token-input" />
        </Field>
        <Field label="IG User ID (tuo account Business)">
          <Input value={form.igUserId} onChange={(e) => setField("igUserId", e.target.value)} placeholder="1784…" data-testid="ig-user-id-input" />
        </Field>
        <Field label={<>Meta App ID <span className="text-muted-foreground">(opzionale, per rinnovo automatico)</span></>}>
          <Input value={form.appId} onChange={(e) => setField("appId", e.target.value)} placeholder="1234567890" data-testid="ig-app-id-input" />
        </Field>
        <Field label={<>Meta App Secret <span className="text-muted-foreground">(opzionale)</span></>}>
          <Input type="password" value={form.appSecret} onChange={(e) => setField("appSecret", e.target.value)}
            placeholder={state.has_app_secret ? "Salvato ••••" : "abc123…"} data-testid="ig-app-secret-input" />
        </Field>
      </div>

      {state.connected && <ExpiryBox state={state} />}

      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={ig.save} disabled={busy} data-testid="ig-save-btn"><Link2 className="mr-2 h-4 w-4" /> Salva credenziali</Button>
        <Button variant="outline" onClick={ig.test} disabled={busy || !state.connected} data-testid="ig-test-btn">Verifica connessione</Button>
        <Button variant="outline" onClick={ig.refresh} disabled={busy || !state.connected} data-testid="ig-refresh-btn">
          <RefreshCw className={`mr-2 h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Rinnova ora
        </Button>
        {state.connected && (
          <Button variant="ghost" onClick={ig.disconnect} disabled={busy} className="text-destructive" data-testid="ig-disconnect-btn">
            <Unplug className="mr-2 h-4 w-4" /> Scollega
          </Button>
        )}
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-lg border border-border bg-secondary/30 px-3 py-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div>
          Serve un account Instagram <b>Business/Creator</b> collegato a una Pagina Facebook e un'app su{" "}
          <a href="https://developers.facebook.com/" target="_blank" rel="noreferrer" className="text-primary underline">Meta for Developers</a>{" "}
          con permessi <code>instagram_basic</code>, <code>instagram_manage_insights</code>, <code>pages_read_engagement</code>.
          Il token è salvato solo sul server e non viene mai mostrato per intero. I profili letti devono essere anch'essi Business/Creator.
          Il rinnovo automatico usa App ID + Secret (Facebook Login) se presenti, altrimenti il refresh Instagram Login; se fallisce, il team riceve un'email.
        </div>
      </div>
    </div>
  );
};

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function ConnectionBadge({ state }) {
  if (!state.connected) {
    return <span className="rounded-full bg-secondary px-2.5 py-1 text-xs text-muted-foreground" data-testid="ig-status-disconnected">Non collegato</span>;
  }
  return (
    <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs text-emerald-400" data-testid="ig-status-connected">
      <CheckCircle2 className="h-3.5 w-3.5" /> Collegato{state.connected_username ? ` · @${state.connected_username}` : ""}
    </span>
  );
}

function ExpiryBox({ state }) {
  const soon = state.days_left != null && state.days_left <= 7;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-secondary/30 px-3 py-2 text-xs" data-testid="ig-expiry-box">
      <span className="flex items-center gap-1.5 text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Token scade il
        <b className={`ml-1 ${soon ? "text-destructive" : "text-foreground"}`} data-testid="ig-expires-at">{(state.expires_at || "").slice(0, 10) || "n.d."}</b>
        {state.days_left != null && <span className="ml-1">({state.days_left} giorni)</span>}
        {state.expires_estimated && <span className="ml-1 text-muted-foreground">· stimata</span>}
      </span>
      <span className="text-muted-foreground">Rinnovo automatico: {state.auto_refresh}{state.last_refresh_at ? ` · ultimo ${(state.last_refresh_at || "").slice(0, 10)}` : ""}</span>
      {state.refresh_error && <span className="flex items-center gap-1 text-destructive" data-testid="ig-refresh-error"><AlertTriangle className="h-3.5 w-3.5" /> {state.refresh_error}</span>}
    </div>
  );
}
